/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { makeStyles } from '@mui/styles';
import Search from 'apollo-react-icons/Search';
import classNames from 'classnames';
import React, { useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearWarnings } from './../../../actions/WarningActions';
import RegulatoryTermsZoomDialog from '../../../components/RegulatoryTermsZoomDialog';
import { ZOOM_STYLES } from '../../../constants/CommonStylesConstants';
import { WARNING_SOURCES } from '../../../constants/WarningsConstants';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import { getAppOptionByKey } from '../../../selectors/AppConfigsSelectors';
import { getUITA } from '../../../utils/commonUtils';
import { FORM_MODEL_ATTRIBUTES } from '../../constants/form-manager.constants';
import { SubGridContext } from '../../context';
import { getEvaluatedExpression } from '../../selectors/form-manager.selectors';
import { getDataByController } from '../../selectors/runtime.selectors';
import {
  createDataMappingList,
  getDefaultSearchByItems,
  getSearchOptionItems,
  getValueFromPath,
} from '../../utils/controls/ssRegulatoryTermsZoom.utils';

const useStyles = makeStyles(ZOOM_STYLES);

const SSRegulatoryTermsDefault = ({
  className,
  isEditor,
  isInTable,
  regulatoryTerms,
  uniqueIdentifier,
  id,
  index,
  termHierarchy,
  actions,
  textBoxPropsJson = '{}',
  mappingFieldName,
  isOnline,
}) => {
  const { isMobileDevice } = useWindowDimensions();
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const inSubGrid = useContext(SubGridContext);
  const inSubGridOrMobile = inSubGrid || isMobileDevice;

  const { entityName, fieldName } = JSON.parse(textBoxPropsJson);
  const defaultValue = useFormSelector(getDataByController, { entityName, fieldName, index });
  const adverseEventCodesforFDA = useSelector(getAppOptionByKey('adverseEventCodesforFDA'));

  const type = adverseEventCodesforFDA === 'IMDRF' ? 'IMDRF' : regulatoryTerms;
  const searchByItems = getDefaultSearchByItems(type, translateString);
  const searchOptionItems = getSearchOptionItems(translateString);

  const selectorProps = { id, index, entityName };
  const predictionCode = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.PREDICTION_CODE,
  });

  const predictionText = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.MAPPING_FIELD_NAME,
  });

  const setDialogClose = useCallback(() => {
    dispatch(clearWarnings(WARNING_SOURCES.ASSISTANT));
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(
    ({ selectedPath = [] }) => {
      const dataMappingList = createDataMappingList(entityName, fieldName);

      const changesToApply = dataMappingList.map((mapping) => {
        const [entityName, fieldName] = mapping.UpdateTo.split('.');

        return {
          value: getValueFromPath(mapping.Name, selectedPath),
          entityName,
          fieldName,
          index,
        };
      });

      actions.bulkUpdateControlValues(changesToApply);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, `${index}`]
  );

  return (
    <>
      <span
        className={classNames(
          classes.root,
          className,
          isInTable ? classes.inTable : '',
          inSubGridOrMobile && !isInTable ? classes.inSubGridOrMobile : '',
          isEditor ? classes.zoomEditMode : ''
        )}
        onClick={() => !isEditor && isOnline && setOpen(true)}
        ui-ta={getUITA(
          'FormCtrl',
          'SSZoom',
          `SSRegulatoryTerms${regulatoryTerms}`,
          uniqueIdentifier
        )}
        data-ss-id={id}
      >
        <div>
          <Search className={classes.searchIcon} color={isOnline ? 'primary' : 'disabled'} />
        </div>
      </span>
      <RegulatoryTermsZoomDialog
        open={open}
        type={type}
        defaultSearchText={defaultValue || ''}
        searchByItems={searchByItems}
        searchOptionItems={searchOptionItems}
        termHierarchy={termHierarchy}
        regulatoryTerms={regulatoryTerms}
        onSelect={handleSelect}
        onClose={setDialogClose}
        predictionText={predictionText}
        predictionCode={predictionCode || ''}
        mappingFieldName={mappingFieldName}
      />
    </>
  );
};

export default SSRegulatoryTermsDefault;
